import React, { useState, useEffect, useMemo } from "react";
import moment from "moment";

export interface Photo {
  url: string;
  description?: string;
  taskId?: string;
  taskAttachmentId?: string;
  approvalStatus?: "PENDING" | "APPROVED" | "REJECTED";
  reviewedByName?: string;
  reviewedAt?: string;
  rejectionReason?: string;
}

interface UsePhotoSliderProps {
  isOpen: boolean;
  onClose: () => void;
  photos: Photo[];
  onReview?: (photo: Photo, action: "APPROVED" | "REJECTED" | "DELETE", reason?: string) => void;
}

export const usePhotoSlider = ({ isOpen, onClose, photos, onReview }: UsePhotoSliderProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [actionType, setActionType] = useState<"approve" | "rejected" | "delete" | null>(null);
  const [reason, setReason] = useState("");
  
  // Zoom and Drag states
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const photoList = useMemo(() => photos, [photos]);

  const photoListSize = photoList.length;

  useEffect(() => {
    if (isOpen) {
      setActiveIndex(0);
      setZoom(1);
      setPosition({ x: 0, y: 0 });
    }
  }, [isOpen]);

  useEffect(() => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  }, [activeIndex]);

  const handlePrev = () => {
    setActiveIndex((prev) => (prev === 0 ? photoListSize - 1 : prev - 1));
  };

  const handleNext = () => {
    setActiveIndex((prev) => (prev === photoListSize - 1 ? 0 : prev + 1));
  };

  const handleActionClick = (type: "approve" | "rejected" | "delete") => {
    setActionType(type);
    setReason("");
    setShowConfirmation(true);
  };

  const handleConfirm = () => {
    if (onReview && actionType) {
      const photo = photoList[activeIndex];
      const action = actionType === "approve" ? "APPROVED" : actionType === "rejected" ? "REJECTED" : "DELETE";
      onReview(photo, action as any, reason);
    }
    setShowConfirmation(false);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && zoom > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") handlePrev();
      if (e.key === "ArrowRight") handleNext();
    };

    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose, photoListSize]);

  const prevIndex = activeIndex === 0 ? photoListSize - 1 : activeIndex - 1;
  const nextIndex = activeIndex === photoListSize - 1 ? 0 : activeIndex + 1;

  const currentPhoto = photoList[activeIndex];
  const isActionDisabled = !!(currentPhoto?.approvalStatus && currentPhoto.approvalStatus !== "PENDING");
  
  const rejectionDetails = currentPhoto?.approvalStatus === "REJECTED" ? {
    reviewedBy: currentPhoto.reviewedByName || "Admin",
    reviewedAt: currentPhoto.reviewedAt ? moment(currentPhoto.reviewedAt).format('h:mm a') : 'N/A',
    reason: currentPhoto.rejectionReason || "No reason provided"
  } : null;

  const approvalDetails = currentPhoto?.approvalStatus === "APPROVED" ? {
    reviewedBy: currentPhoto.reviewedByName || "Admin",
    reviewedAt: currentPhoto.reviewedAt ? moment(currentPhoto.reviewedAt).format('h:mm a') : 'N/A',
  } : null;

  return {
    activeIndex,
    setActiveIndex,
    showConfirmation,
    setShowConfirmation,
    actionType,
    setActionType,
    reason,
    setReason,
    zoom,
    setZoom,
    position,
    setPosition,
    isDragging,
    handlePrev,
    handleNext,
    handleActionClick,
    handleConfirm,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    prevIndex,
    nextIndex,
    photoList,
    isActionDisabled,
    rejectionDetails,
    currentPhoto,
    approvalDetails
  };
};

export default usePhotoSlider;
